// Styled cmdk primitives. Used by the ⌘K palette and the Combobox; the AI module renders its
// "Ask AI" rows with CommandGroup/CommandItem so they match.
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type ReactNode } from 'react'
import { Command as C } from 'cmdk'
import { Search } from 'lucide-react'
import { cn } from './cn'
import { Kbd } from './kbd'

export const Command = forwardRef<ElementRef<typeof C>, ComponentPropsWithoutRef<typeof C>>(function Command({ className, ...props }, ref) {
  return <C ref={ref} className={cn('flex size-full flex-col overflow-hidden text-text', className)} {...props} />
})

export const CommandInput = forwardRef<ElementRef<typeof C.Input>, ComponentPropsWithoutRef<typeof C.Input> & { icon?: ReactNode; end?: ReactNode }>(
  function CommandInput({ className, icon, end, ...props }, ref) {
    return (
      <div className="flex items-center gap-2 border-b border-border px-3" cmdk-input-wrapper="">
        <span className="shrink-0 text-text-subtle [&_svg]:size-4">{icon ?? <Search />}</span>
        <C.Input ref={ref} className={cn('h-12 w-full min-w-0 bg-transparent text-md text-text outline-none placeholder:text-text-subtle', className)} {...props} />
        {end}
      </div>
    )
  },
)

export const CommandList = forwardRef<ElementRef<typeof C.List>, ComponentPropsWithoutRef<typeof C.List>>(function CommandList({ className, ...props }, ref) {
  return <C.List ref={ref} className={cn('max-h-[min(60vh,420px)] scroll-py-2 overflow-y-auto overscroll-contain p-1 transition-[height] duration-150', className)} {...props} />
})

export const CommandEmpty = forwardRef<ElementRef<typeof C.Empty>, ComponentPropsWithoutRef<typeof C.Empty>>(function CommandEmpty({ className, ...props }, ref) {
  return <C.Empty ref={ref} className={cn('px-3 py-8 text-center text-base text-text-muted', className)} {...props} />
})

export const CommandGroup = forwardRef<ElementRef<typeof C.Group>, ComponentPropsWithoutRef<typeof C.Group>>(function CommandGroup({ className, ...props }, ref) {
  return (
    <C.Group ref={ref} className={cn(
      'overflow-hidden text-text [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-text-subtle',
      className,
    )} {...props} />
  )
})

export const CommandSeparator = forwardRef<ElementRef<typeof C.Separator>, ComponentPropsWithoutRef<typeof C.Separator>>(function CommandSeparator({ className, ...props }, ref) {
  return <C.Separator ref={ref} className={cn('-mx-1 my-1 h-px bg-border', className)} {...props} />
})

export interface CommandItemProps extends ComponentPropsWithoutRef<typeof C.Item> {
  icon?: ReactNode
  /** Secondary text after the label (muted). */
  hint?: ReactNode
  /** Right-aligned combo ("g o"). */
  shortcut?: string
  /** Right-aligned arbitrary content (badges). */
  end?: ReactNode
}
export const CommandItem = forwardRef<ElementRef<typeof C.Item>, CommandItemProps>(function CommandItem({ className, icon, hint, shortcut, end, children, ...props }, ref) {
  return (
    <C.Item ref={ref} className={cn(
      'group relative flex h-9 cursor-default select-none items-center gap-3 rounded-[8px] px-2 text-base text-text outline-none',
      'data-[selected=true]:bg-surface-active data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
      '[&_svg]:size-4 [&_svg]:shrink-0',
      className,
    )} {...props}>
      {icon && <span className="grid size-5 shrink-0 place-items-center text-text-muted group-data-[selected=true]:text-text">{icon}</span>}
      <span className="flex min-w-0 flex-1 items-baseline gap-2">
        <span className="truncate">{children}</span>
        {hint && <span className="truncate text-sm text-text-subtle">{hint}</span>}
      </span>
      {end}
      {shortcut && <Kbd combo={shortcut} className="shrink-0" />}
    </C.Item>
  )
})

export const CommandLoading = C.Loading
